import { User as FBUser } from 'firebase/auth';
import { DocumentReference, GeoPoint, Timestamp } from 'firebase/firestore';

import type { Language } from '../i18n/i18n';

export interface DocumentDataType {
  fileName: string;
  fileType: FileTypes;
  timestamp: Timestamp;
  // null: the photo has no position (OQ-94).
  location: GeoPoint | null;
  // Metres; absent or null = unknown (OQ-94).
  locationAccuracy?: number | null;
  adminMessage: string;
  message: string;
  uploadedBy: string;
  isRead: boolean;
  projectId: string;
  statusId?: string;
  assignee: string;
  history?: HistoryItem[];
  // Stored only on a pinned photo (OQ-161).
  drawingPin?: DrawingPin;
}

// Where on a Drawing a photo was taken: page index, x/y as fractions of the page (OQ-161).
export type DrawingPin = { drawingId: string; page: number; x: number; y: number };

// organisations/{orgId}/drawings/{drawingId} (OQ-161, flows/drawings.md).
export interface DrawingDataType {
  name: string;
  location: GeoPoint;
  projectId: string | null;
  source: 'pdf' | 'image';
  pages: { fileName: string; width: number; height: number }[];
  originalFileName: string | null;
  createdBy: string;
  createdAt: Timestamp;
}

export interface CustomDrawingDataType extends DrawingDataType {
  docId: string;
}

export interface CustomDocumentDataType extends DocumentDataType {
  docId: string;
  docRef: DocumentReference;
}

export interface UserDataType {
  displayName?: string;
  organisationId: string;
  role: UserRole;
  email?: string;
  status?: UserStatus;
  // The UI language; missing means 'sv' (OQ-162).
  language?: Language;
}

export interface UserDataTypeWithFID extends UserDataType {
  fId: string;
}

export interface CustomUserDataType extends UserDataType {
  docId: string;
  docRef: DocumentReference;
}

export interface AuthenticatedUserDataType extends CustomUserDataType {
  credentials: FBUser;
}

export interface OrganisationDataType {
  urlId: string;
  displayName: string;
}

// TODO: replace more interfaces with types for convenience
export type OrganisationType = {
  urlId: string;
  displayName: string;
  organisationColor?: string;
  organisationLogo?: string;
  requireLocation?: boolean;
};

export type WithFID = {
  fId: string;
};

export type WithCreationDate = {
  createdAt: Timestamp;
};

export type CameraLinkDocumentType = {
  expiry: Timestamp;
  organisationId: string;
  projectId?: string | null;
  permanent?: boolean;
  // Set when the link was revoked and replaced (OQ-115).
  revokedAt?: Timestamp;
  revokedBy?: string;
};

// What GET /v1/camera-link/{id} answers, and nothing else (OQ-135).
export type CameraLinkPublicType = Pick<CameraLinkDocumentType, 'expiry' | 'organisationId'> & {
  projectId: string | null;
  // The organisation's "Kräv plats vid uppladdning"; missing means true (OQ-110).
  requireLocation?: boolean;
  // Shown to the guest in the page's header and receipt; null when there is none (OQ-188).
  organisationName?: string | null;
  projectName?: string | null;
};

export interface CustomOrganisationDataType extends OrganisationDataType {
  docId: string;
  docRef: DocumentReference;
  organisationColor?: string;
  organisationLogo?: string;
  // "Kräv plats vid uppladdning" for camera links; missing means true (OQ-110).
  requireLocation?: boolean;
}

export enum FileTypes {
  IMAGE = 'image',
  VIDEO = 'video'
}

export enum UserRole {
  SUPERADMIN = 'SUPERADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER'
}

export type ProjectDataType = {
  name: string;
  organisationId: string;
  statusId?: string | null;
  permanentLink?: string;
};

export interface CustomProjectDataType extends ProjectDataType {
  docId: string;
  docRef: DocumentReference;
}

export type StatusDataType = {
  name: string;
  text: string;
  index: number;
};

export interface CustomStatusDataType extends StatusDataType {
  docId: string;
  docRef: DocumentReference;
}
export type DataListViewMode = 'FILES' | 'PROJECTS';

export enum UserStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  INACTIVE = 'inactive'
}
export type HistoryItem = {
  before: string | null;
  after: string | null;
  field: 'assignee' | 'statusId' | 'adminMessage' | 'isOpen';
  timestamp: Timestamp;
  userId: string;
};
